import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import {
  ArrowLeft,
  Loader2,
  Plus,
  Save,
  Search,
  Trash2,
  User as UserIcon,
  UserPlus,
  X,
  AlertCircle,
  Package,
  Truck,
  CreditCard,
  Ticket,
  ClipboardList,
} from 'lucide-react'
import { apiGet, apiPost, formatMXN } from '../lib/api'

// ---- Tipos ----

interface ClienteRow {
  id: string
  nombre: string
  email: string
  telefono: string
  descuento: number
}

interface ClientesResponse {
  data: ClienteRow[]
}

interface ProductoRow {
  id: string
  sku: string
  nombre: string
  precio: number
  precio2: number | null
  precio3: number | null
  precio4: number | null
  ivaRate: number | null
  carrier: string | null
  precioOferta: number | null
  stock: number
  imagen: string
}

// Tarifas de envío por paquetería (deben coincidir con config Shipping_Carrier_*_Rate).
const CARRIER_RATES: Record<string, number> = { estafeta: 160, fedex: 260 }

const PRICE_LEVELS_N = [
  { level: 1 as const, label: 'Público' },
  { level: 2 as const, label: 'Mayoreo' },
  { level: 3 as const, label: 'Distribuidor' },
  { level: 4 as const, label: 'Especial' },
]

interface ProductosResponse {
  data: ProductoRow[]
}

interface ItemLinea {
  productId: string
  sku: string
  nombre: string
  quantity: number
  unitPrice: number
  taxRate: number
  level: 1 | 2 | 3 | 4
  price1: number | null
  price2: number | null
  price3: number | null
  price4: number | null
  carrier: string | null
}

function priceByLevelN(it: ItemLinea, level: 1 | 2 | 3 | 4): number | null {
  return level === 1 ? it.price1 : level === 2 ? it.price2 : level === 3 ? it.price3 : it.price4
}

const TAX_RATE_DEFAULT = 0.16

const PAYMENT_METHODS: Array<{ value: string; label: string }> = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'mp_link', label: 'Mercado Pago (link)' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'tienda', label: 'Pago en tienda' },
  { value: 'otro', label: 'Otro' },
]

const PAYMENT_STATUSES: Array<{ value: 'pending' | 'paid'; label: string }> = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'paid', label: 'Pagado' },
]

// ---- Subcomponentes UI ----

function Card({
  title,
  icon,
  children,
  actions,
}: {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-ink-200 bg-white">
      <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          {icon}
          {title}
        </div>
        {actions}
      </header>
      <div className="p-5">{children}</div>
    </section>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="text-ink-500">{label}</span>
      <span className="text-right font-medium text-ink-900">{value}</span>
    </div>
  )
}

function Field({
  label,
  children,
  className,
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="mb-1 block text-xs font-medium text-ink-700">{label}</span>
      {children}
    </label>
  )
}

const inputCls =
  'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500'

// ---- Modal de búsqueda de productos ----

function ProductSearchModal({
  open,
  onClose,
  onPick,
}: {
  open: boolean
  onClose: () => void
  onPick: (p: ProductoRow) => void
}) {
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')

  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q, open])

  const { data, isFetching } = useQuery<ProductosResponse>({
    queryKey: ['admin-productos-buscar', debouncedQ],
    queryFn: () =>
      apiGet(`/api/admin/productos?page=1${debouncedQ ? `&q=${encodeURIComponent(debouncedQ)}` : ''}`),
    enabled: open,
    staleTime: 30_000,
  })

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/60 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <h3 className="text-base font-semibold text-ink-900">Agregar producto</h3>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-4" />
          </button>
        </header>
        <div className="p-5">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              autoFocus
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por SKU o nombre…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-ink-100">
            {isFetching && (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-ink-500">
                <Loader2 className="size-4 animate-spin" />
                Buscando…
              </div>
            )}
            {!isFetching && data && data.data.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-ink-500">Sin resultados</div>
            )}
            {data && data.data.length > 0 && (
              <ul className="divide-y divide-ink-100">
                {data.data.map((p) => {
                  const precio = p.precioOferta ?? p.precio
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => onPick(p)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-ink-50"
                      >
                        <div className="size-12 shrink-0 overflow-hidden rounded-md border border-ink-200 bg-ink-50">
                          {p.imagen ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={p.imagen} alt={p.nombre} className="size-full object-cover" />
                          ) : (
                            <div className="grid size-full place-items-center text-ink-400">
                              <Package className="size-4" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-ink-900">{p.nombre}</div>
                          <div className="font-mono text-xs text-ink-500">{p.sku}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-ink-900">
                            {formatMXN(precio)}
                          </div>
                          <div className="text-xs text-ink-500">Stock {p.stock}</div>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---- Autocomplete de clientes ----

function ClienteAutocomplete({
  selected,
  onSelect,
  onClear,
}: {
  selected: ClienteRow | null
  onSelect: (c: ClienteRow) => void
  onClear: () => void
}) {
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])

  const { data, isFetching } = useQuery<ClientesResponse>({
    queryKey: ['admin-clientes-buscar', debouncedQ],
    queryFn: () =>
      apiGet(`/api/admin/clientes?page=1&active=true${debouncedQ ? `&q=${encodeURIComponent(debouncedQ)}` : ''}`),
    enabled: !selected && debouncedQ.length > 0,
    staleTime: 30_000,
  })

  if (selected) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm">
        <div>
          <div className="font-semibold text-ink-900">{selected.nombre || 'Sin nombre'}</div>
          <div className="text-xs text-ink-500">{selected.email}</div>
          {selected.descuento > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Descuento {selected.descuento}%
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="rounded-md p-1.5 text-ink-500 hover:bg-white hover:text-ink-900"
          title="Quitar cliente"
        >
          <X className="size-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
      <input
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder="Buscar por nombre, email o teléfono…"
        className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
      />
      {open && debouncedQ.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-ink-200 bg-white shadow-lg">
          {isFetching && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-ink-500">
              <Loader2 className="size-3.5 animate-spin" /> Buscando…
            </div>
          )}
          {!isFetching && data && data.data.length === 0 && (
            <div className="px-4 py-3 text-sm text-ink-500">Sin coincidencias</div>
          )}
          {data &&
            data.data.length > 0 &&
            data.data.slice(0, 10).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onSelect(c)
                  setOpen(false)
                  setQ('')
                }}
                className="block w-full px-4 py-2 text-left text-sm hover:bg-ink-50"
              >
                <div className="font-medium text-ink-900">{c.nombre || 'Sin nombre'}</div>
                <div className="text-xs text-ink-500">
                  {c.email}
                  {c.telefono ? ` · ${c.telefono}` : ''}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  )
}

// ---- Página principal ----

export function PedidoNuevo() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Cliente
  const [customerTab, setCustomerTab] = useState<'existente' | 'nuevo'>('existente')
  const [selectedClient, setSelectedClient] = useState<ClienteRow | null>(null)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newPhone, setNewPhone] = useState('')

  // Items
  const [items, setItems] = useState<ItemLinea[]>([])
  const [productModalOpen, setProductModalOpen] = useState(false)

  // Envío
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [country, setCountry] = useState('México')
  const [carrier, setCarrier] = useState('')
  const [trackingCode, setTrackingCode] = useState('')

  // Pago
  const [paymentMethod, setPaymentMethod] = useState('efectivo')
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid'>('pending')

  // Descuento + envío + notas
  const [discountCode, setDiscountCode] = useState('')
  const [discountTotalStr, setDiscountTotalStr] = useState('0')
  const [shippingCostStr, setShippingCostStr] = useState('0')
  const [notes, setNotes] = useState('')

  const [error, setError] = useState<string | null>(null)

  function addProduct(p: ProductoRow) {
    const precio = p.precioOferta ?? p.precio
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.productId === p.id)
      if (idx >= 0) {
        const next = [...prev]
        const cur = next[idx]
        if (cur) next[idx] = { ...cur, quantity: cur.quantity + 1 }
        return next
      }
      return [
        ...prev,
        {
          productId: p.id,
          sku: p.sku,
          nombre: p.nombre,
          quantity: 1,
          unitPrice: precio,
          taxRate: p.ivaRate != null && p.ivaRate > 0 ? p.ivaRate : TAX_RATE_DEFAULT,
          level: 1 as const,
          price1: p.precio,
          price2: p.precio2,
          price3: p.precio3,
          price4: p.precio4,
          carrier: p.carrier ?? null,
        },
      ]
    })
    setProductModalOpen(false)
  }

  function updateItem(idx: number, patch: Partial<ItemLinea>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  }

  // Cambia el nivel de precio de una línea → ajusta el precio unitario.
  function setItemLevel(idx: number, level: 1 | 2 | 3 | 4) {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it
        const p = priceByLevelN(it, level)
        return { ...it, level, unitPrice: p != null ? p : it.unitPrice }
      }),
    )
  }

  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx))
  }

  // ---- Totales ----
  const subtotal = useMemo(
    () => items.reduce((acc, it) => acc + it.unitPrice * it.quantity, 0),
    [items],
  )
  const discountTotal = useMemo(() => {
    const n = Number(discountTotalStr)
    return isFinite(n) && n > 0 ? Math.min(n, subtotal) : 0
  }, [discountTotalStr, subtotal])
  const shippingCost = useMemo(() => {
    const n = Number(shippingCostStr)
    return isFinite(n) && n > 0 ? n : 0
  }, [shippingCostStr])

  // Envío sugerido: cada paquetería (categoría) involucrada suma su tarifa
  // (productos de distinta paquetería van en paquetes separados).
  const suggestedShipping = useMemo(() => {
    const carriers = new Set(
      items.map((it) => it.carrier).filter((c): c is string => !!c),
    )
    let sum = 0
    for (const c of carriers) sum += CARRIER_RATES[c] || 0
    return sum
  }, [items])

  // Auto-rellena el costo de envío cuando cambian las paqueterías del pedido.
  // (El admin puede sobreescribirlo a mano; solo se recalcula si cambia la mezcla.)
  useEffect(() => {
    if (suggestedShipping > 0) setShippingCostStr(String(suggestedShipping))
  }, [suggestedShipping])
  const subtotalDescontado = Math.max(0, subtotal - discountTotal)
  // IVA 16% contenido en el subtotal (precios ingresados con IVA)
  const taxTotal =
    subtotalDescontado > 0
      ? +(subtotalDescontado - subtotalDescontado / (1 + TAX_RATE_DEFAULT)).toFixed(2)
      : 0
  const total = +(subtotalDescontado + shippingCost).toFixed(2)

  // ---- Mutation ----
  const createMut = useMutation<{ data: { id: string; orderNumber: string } }, Error, any>({
    mutationFn: (payload) => apiPost('/api/admin/pedidos', payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['pedidos'] })
      navigate({ to: '/pedidos/$id', params: { id: res.data.id } })
    },
    onError: (e) => {
      setError(e?.message || 'Error al crear el pedido')
    },
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    // Validación local básica
    if (customerTab === 'existente' && !selectedClient) {
      setError('Selecciona un cliente existente o cambia a la pestaña "Nuevo"')
      return
    }
    if (customerTab === 'nuevo') {
      if (newName.trim().length < 3) {
        setError('Nombre del cliente es requerido (mínimo 3 caracteres)')
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) {
        setError('Email del cliente inválido')
        return
      }
    }
    if (items.length === 0) {
      setError('Debes agregar al menos un producto')
      return
    }
    for (const it of items) {
      if (it.quantity <= 0) {
        setError(`Cantidad inválida para ${it.sku}`)
        return
      }
      if (it.unitPrice < 0) {
        setError(`Precio inválido para ${it.sku}`)
        return
      }
    }

    const customer =
      customerTab === 'existente' && selectedClient
        ? {
            userId: selectedClient.id,
            name: selectedClient.nombre,
            email: selectedClient.email,
            phone: selectedClient.telefono,
          }
        : {
            name: newName.trim(),
            email: newEmail.trim(),
            phone: newPhone.trim(),
          }

    const payload = {
      customer,
      shipping: {
        street: street.trim(),
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        country: country.trim() || 'México',
        carrier: carrier.trim(),
        trackingCode: trackingCode.trim(),
      },
      items: items.map((it) => ({
        productId: it.productId,
        quantity: it.quantity,
        unitPrice: it.unitPrice,
        taxRate: it.taxRate,
      })),
      paymentMethod,
      paymentStatus,
      notes: notes.trim(),
      discountCode: discountCode.trim(),
      discountTotal,
      shippingCost,
    }

    createMut.mutate(payload)
  }

  return (
    <>
      <Topbar
        title="Nuevo pedido"
        subtitle="Crear pedido manualmente (ventas telefónicas, walk-in, ajustes)"
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate({ to: '/pedidos' })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <ArrowLeft className="size-3.5" /> Volver
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={createMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {createMut.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              Crear pedido
            </button>
          </>
        }
      />
      <form onSubmit={onSubmit} className="space-y-4 p-6">
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            <AlertCircle className="size-4" /> {error}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-3">
          {/* Cliente */}
          <Card title="Cliente" icon={<UserIcon className="size-4 text-brand-600" />}>
            <div className="mb-3 inline-flex rounded-lg border border-ink-200 bg-ink-50 p-0.5">
              <button
                type="button"
                onClick={() => setCustomerTab('existente')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  customerTab === 'existente'
                    ? 'bg-white text-ink-900 shadow-sm'
                    : 'text-ink-600 hover:text-ink-900'
                }`}
              >
                <UserIcon className="size-3.5" /> Existente
              </button>
              <button
                type="button"
                onClick={() => setCustomerTab('nuevo')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  customerTab === 'nuevo'
                    ? 'bg-white text-ink-900 shadow-sm'
                    : 'text-ink-600 hover:text-ink-900'
                }`}
              >
                <UserPlus className="size-3.5" /> Nuevo
              </button>
            </div>

            {customerTab === 'existente' && (
              <ClienteAutocomplete
                selected={selectedClient}
                onSelect={setSelectedClient}
                onClear={() => setSelectedClient(null)}
              />
            )}
            {customerTab === 'nuevo' && (
              <div className="space-y-3">
                <Field label="Nombre completo *">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className={inputCls}
                    placeholder="Juan Pérez"
                  />
                </Field>
                <Field label="Email *">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className={inputCls}
                    placeholder="juan@correo.com"
                  />
                </Field>
                <Field label="Teléfono">
                  <input
                    type="tel"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    className={inputCls}
                    placeholder="55 1234 5678"
                  />
                </Field>
              </div>
            )}
          </Card>

          {/* Envío */}
          <Card title="Envío" icon={<Truck className="size-4 text-brand-600" />}>
            <div className="space-y-3">
              <Field label="Calle y número">
                <input
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  className={inputCls}
                  placeholder="Av. Reforma 123, Col. Centro"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ciudad">
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Estado">
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className={inputCls}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="CP">
                  <input
                    type="text"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="País">
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className={inputCls}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Transportista">
                  <input
                    type="text"
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value)}
                    className={inputCls}
                    placeholder="DHL, FedEx…"
                  />
                </Field>
                <Field label="Tracking">
                  <input
                    type="text"
                    value={trackingCode}
                    onChange={(e) => setTrackingCode(e.target.value)}
                    className={inputCls}
                  />
                </Field>
              </div>
            </div>
          </Card>

          {/* Pago */}
          <Card title="Pago" icon={<CreditCard className="size-4 text-brand-600" />}>
            <div className="space-y-3">
              <Field label="Método de pago">
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className={inputCls}
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Estado del pago">
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as 'pending' | 'paid')}
                  className={inputCls}
                >
                  {PAYMENT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Card>
        </div>

        {/* Items */}
        <Card
          title={`Items (${items.length})`}
          icon={<Package className="size-4 text-brand-600" />}
          actions={
            <button
              type="button"
              onClick={() => setProductModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" /> Agregar producto
            </button>
          }
        >
          {items.length === 0 ? (
            <div className="grid place-items-center rounded-lg border border-dashed border-ink-200 bg-ink-50 px-4 py-10 text-center text-sm text-ink-500">
              <Package className="mb-2 size-8 text-ink-300" />
              <p>No has agregado productos al pedido.</p>
              <button
                type="button"
                onClick={() => setProductModalOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
              >
                <Plus className="size-3.5" /> Agregar primer producto
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-3 py-2 font-medium">SKU</th>
                    <th className="px-3 py-2 font-medium">Producto</th>
                    <th className="px-3 py-2 font-medium text-right">Cantidad</th>
                    <th className="px-3 py-2 font-medium text-right">P. unitario</th>
                    <th className="px-3 py-2 font-medium text-right">Subtotal</th>
                    <th className="w-10 px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={`${it.productId}-${idx}`} className="border-t border-ink-100">
                      <td className="px-3 py-2 font-mono text-xs text-ink-700">{it.sku}</td>
                      <td className="px-3 py-2 text-ink-900">{it.nombre}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={1}
                          value={it.quantity}
                          onChange={(e) =>
                            updateItem(idx, {
                              quantity: Math.max(1, parseInt(e.target.value || '1')),
                            })
                          }
                          className="w-20 rounded border border-ink-200 px-2 py-1 text-right text-sm outline-none focus:border-brand-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-col items-end gap-1">
                          <select
                            value={it.level}
                            onChange={(e) =>
                              setItemLevel(idx, Number(e.target.value) as 1 | 2 | 3 | 4)
                            }
                            title="Nivel de precio"
                            className="w-32 rounded border border-ink-200 px-1 py-0.5 text-xs outline-none focus:border-brand-500"
                          >
                            {PRICE_LEVELS_N.filter(
                              (L) => L.level === 1 || priceByLevelN(it, L.level) != null,
                            ).map((L) => (
                              <option key={L.level} value={L.level}>
                                {L.label}
                                {priceByLevelN(it, L.level) != null
                                  ? ` · ${formatMXN(priceByLevelN(it, L.level) as number)}`
                                  : ''}
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={it.unitPrice}
                            onChange={(e) =>
                              updateItem(idx, {
                                unitPrice: Math.max(0, Number(e.target.value || 0)),
                              })
                            }
                            className="w-28 rounded border border-ink-200 px-2 py-1 text-right text-sm outline-none focus:border-brand-500"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-ink-900">
                        {formatMXN(it.unitPrice * it.quantity)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="rounded-md p-1.5 text-red-500 hover:bg-red-50"
                          title="Quitar item"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Descuento + Notas */}
          <div className="space-y-4">
            <Card title="Descuento" icon={<Ticket className="size-4 text-brand-600" />}>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Código">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    className={inputCls}
                    placeholder="Ej: BLACKFRIDAY"
                  />
                </Field>
                <Field label="Monto del descuento">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={discountTotalStr}
                    onChange={(e) => setDiscountTotalStr(e.target.value)}
                    className={inputCls}
                  />
                </Field>
              </div>
              <p className="mt-2 text-xs text-ink-500">
                El descuento se aplica directo al subtotal. No re-valida cupones.
              </p>
            </Card>

            <Card title="Notas" icon={<ClipboardList className="size-4 text-brand-600" />}>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className={inputCls}
                placeholder="Notas internas o instrucciones de envío…"
              />
            </Card>
          </div>

          {/* Resumen */}
          <Card title="Resumen" icon={<Package className="size-4 text-brand-600" />}>
            <Row label="Subtotal" value={formatMXN(subtotal)} />
            {discountTotal > 0 && (
              <Row
                label={`Descuento${discountCode ? ` (${discountCode})` : ''}`}
                value={`- ${formatMXN(discountTotal)}`}
              />
            )}
            <Row label="IVA (16% incluido)" value={formatMXN(taxTotal)} />
            <div className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-ink-500">Envío</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={shippingCostStr}
                onChange={(e) => setShippingCostStr(e.target.value)}
                className="w-28 rounded border border-ink-200 px-2 py-1 text-right text-sm outline-none focus:border-brand-500"
              />
            </div>
            <hr className="my-3 border-ink-100" />
            <div className="flex items-center justify-between py-2 text-base">
              <span className="font-semibold text-ink-900">Total</span>
              <span className="font-bold text-ink-900">{formatMXN(total)}</span>
            </div>

            <button
              type="submit"
              disabled={createMut.isPending}
              className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {createMut.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              Crear pedido
            </button>
          </Card>
        </div>
      </form>

      <ProductSearchModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onPick={addProduct}
      />
    </>
  )
}
